import { useSidebar } from "@/components/ui/sidebar-context";
import {
  ChevronDown,
  Home,
  Users,
  UserCog,
  DoorOpen,
  Settings,
  Activity,
  LogOut,
  TrendingUp,
  Radio,
  Wallet,
  BarChart3,
  FileText,
  PencilLine,
  Package,
  Gift,
  // Zap, // Automations is currently hidden.
  Sparkles,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { toast } from "sonner";
import { useLogout } from "@/hooks/auth/useLogout";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/provider/SubscriptionProvider";
import { canViewFinances } from "@/lib/permissions";
import { cn } from "@/lib/utils";

const baseMenuItems = [
  { title: "Dashboard", url: "/dashboard", icon: Home },
  { title: "Members", url: "/members", icon: Users },
  { title: "Growth", url: "/growth", icon: TrendingUp },
  { title: "Activity", url: "/activity", icon: Activity },
  { title: "Communications & Classes", url: "/communications?tab=messages", icon: Radio },
  // { title: "Automations", url: "/automations", icon: Zap, tag: "New" },
  {
    title: "Business Intelligence",
    icon: BarChart3,
    children: [
      { title: "MPR", url: "/business-intelligence/mpr", icon: FileText },
      {
        title: "Data Entry",
        url: "/business-intelligence/data-entry",
        icon: PencilLine,
      },
      { title: "Churn Insights", url: "/exit-data", icon: DoorOpen },
    ],
  },
  { title: "Inventory", url: "/inventory", icon: Package },
  {
    title: "Wallet & Transactions",
    url: "/wallet-transactions",
    icon: Wallet,
  },
  { title: "Staff", url: "/staff", icon: UserCog, adminOnly: true },
  { title: "Settings", url: "/settings", icon: Settings },
];

type MenuItem = (typeof baseMenuItems)[number];

const itemHasUrl = (item: MenuItem): item is MenuItem & { url: string } =>
  "url" in item;

const itemHasChildren = (
  item: MenuItem,
): item is MenuItem & { children: NonNullable<MenuItem["children"]> } =>
  "children" in item;

interface AppSidebarProps {
  onOpenReferral: () => void;
}

export function AppSidebar({ onOpenReferral }: AppSidebarProps) {
  const { state, toggleSidebar, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: logout } = useLogout();
  const { user } = useAuth();
  const { status } = useSubscription();

  const isBusinessIntelligenceRoute =
    location.pathname.startsWith("/business-intelligence") ||
    location.pathname === "/exit-data";

  const [isBIOpen, setIsBIOpen] = useState(isBusinessIntelligenceRoute);

  // The mobile sidebar is a controlled sheet that survives route changes with
  // the dashboard shell. Close it after any sidebar navigation completes.
  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, location.pathname, location.search, setOpenMobile]);

  useEffect(() => {
    if (isBusinessIntelligenceRoute) {
      setIsBIOpen(true);
    }
  }, [isBusinessIntelligenceRoute]);

  const menuItems = useMemo(
    () =>
      baseMenuItems.filter((item) => {
        if ("adminOnly" in item && item.adminOnly) {
          return user?.role === "Admin" || user?.role === "Manager";
        }

        if (itemHasUrl(item) && item.url === "/wallet-transactions") {
          return canViewFinances(user);
        }

        return true;
      }),
    [user],
  );

  const handleLogout = () => {
    toast.success("Logged out successfully");
    logout();
    navigate("/login");
  };

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="border-b p-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <Activity className="h-5 w-5 text-primary-foreground" />
          </div>
          {!collapsed && (
            <div>
              <h2 className="text-lg font-bold text-foreground">FirstRep</h2>
              <p className="text-xs text-muted-foreground">Gym Insights</p>
            </div>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const hasChildren = itemHasChildren(item);
                const Icon = item.icon;

                return (
                  <SidebarMenuItem key={item.title}>
                    {hasChildren ? (
                      <SidebarMenuButton asChild>
                        <button
                          type="button"
                          title={collapsed ? item.title : undefined}
                          aria-label={collapsed ? item.title : undefined}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-smooth hover:bg-sidebar-accent",
                            isBusinessIntelligenceRoute &&
                              "bg-sidebar-accent text-sidebar-accent-foreground font-medium",
                          )}
                          onClick={() => {
                            if (collapsed) {
                              toggleSidebar();
                              return;
                            }

                            setIsBIOpen((open) => !open);
                          }}
                          aria-expanded={isBIOpen}
                        >
                          <Icon className="h-5 w-5" />
                          {!collapsed && (
                            <>
                              <span className="min-w-0 flex-1">
                                {item.title}
                              </span>
                              <ChevronDown
                                className={cn(
                                  "h-4 w-4 shrink-0 transition-transform",
                                  isBIOpen && "rotate-180",
                                )}
                              />
                            </>
                          )}
                        </button>
                      </SidebarMenuButton>
                    ) : itemHasUrl(item) ? (
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          title={collapsed ? item.title : undefined}
                          aria-label={collapsed ? item.title : undefined}
                          className="flex items-center gap-3 rounded-lg px-3 py-2 transition-smooth hover:bg-sidebar-accent"
                          activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                        >
                          <Icon className="h-5 w-5" />
                          {!collapsed && (
                            <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                              <span className="truncate">{item.title}</span>
                              {"tag" in item ? (
                                <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
                                  {item.tag}
                                </span>
                              ) : null}
                            </span>
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    ) : null}

                    {!collapsed && hasChildren && isBIOpen ? (
                      <div className="ml-7 mt-1 space-y-1">
                        {item.children
                          .filter(
                            (child) =>
                              child.url !== "/business-intelligence/mpr" ||
                              canViewFinances(user),
                          )
                          .map((child) => {
                          const ChildIcon = child.icon;

                          return (
                            <NavLink
                              key={child.title}
                              to={child.url}
                              className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm !text-muted-foreground transition-smooth hover:bg-sidebar-accent hover:!text-sidebar-accent-foreground"
                              activeClassName="bg-sidebar-accent !text-sidebar-accent-foreground font-medium"
                            >
                              <ChildIcon className="h-4 w-4" />
                              {child.title}
                            </NavLink>
                          );
                        })}
                      </div>
                    ) : null}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t p-4 space-y-1">
        <SidebarMenuButton asChild>
          <NavLink
            to="/premium"
            title={collapsed ? "FirstRep Premium" : undefined}
            aria-label={collapsed ? "FirstRep Premium" : undefined}
            className="premium-sidebar-link group relative flex items-center gap-3 overflow-hidden rounded-lg border-2 border-transparent px-3 py-2.5 text-amber-950 transition-all hover:-translate-y-px dark:text-amber-100"
            activeClassName="text-amber-950 dark:text-amber-100"
          >
            <Sparkles className="h-5 w-5 shrink-0 text-amber-600 transition-transform group-hover:scale-110 dark:text-amber-400" />
            {!collapsed && (
              <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <span className="truncate font-semibold">FirstRep Premium</span>
                <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-white shadow-sm">
                  New
                </span>
              </span>
            )}
          </NavLink>
        </SidebarMenuButton>
        {status === "active" && (
          <Button
            variant="ghost"
          className="w-full justify-start gap-3 text-primary hover:bg-primary/10 hover:text-primary"
          onClick={onOpenReferral}
          title={collapsed ? "Refer & Earn" : undefined}
          aria-label={collapsed ? "Refer & Earn" : undefined}
          >
            <Gift className="h-5 w-5 shrink-0" />
            {!collapsed && (
              <span>Refer & Earn</span>
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={handleLogout}
          title={collapsed ? "Logout" : undefined}
          aria-label={collapsed ? "Logout" : undefined}
        >
          <LogOut className="h-5 w-5" />
          {!collapsed && <span>Logout</span>}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
